import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { fetchCredits, fetchProfile, updateProfile, deleteAccount, createCheckout, createTopupCheckout } from '../lib/api.js'
import { useAuth } from '../hooks/useAuth.js'
import { useNavigate } from 'react-router-dom'

// ── Tier data moved inside component for i18n ────────────────────────────────

const BUNDLES = [
  { id: 'bundle-basic', ext: 'Plus', studio: 'Basic', price: 24, savings: 4 },
  { id: 'bundle-pro',   ext: 'Pro',  studio: 'Pro',   price: 59, savings: 9 },
]

const TOPUPS = [
  { minutes: 20,  price: 15  },
  { minutes: 60,  price: 39  },
  { minutes: 200, price: 110 },
  { minutes: 600, price: 270 },
]

export default function UsagePage() {
  const { t } = useTranslation()
  const { signOut } = useAuth()
  const nav = useNavigate()

  const extensionTiers = [
    { id: 'ext-free', label: t('landing.tiers.tierFree'), price: 0, minutes: t('landing.tiers.fiveMinTrial') },
    { id: 'ext-plus', label: t('landing.tiers.tierPlus'), price: 9, minutes: t('landing.tiers.minPerMo', { count: 300 }) },
    { id: 'ext-pro', label: t('landing.tiers.tierPro'), price: 19, minutes: t('landing.tiers.minPerMo', { count: 1000 }) },
  ]
  const studioTiers = [
    { id: 'studio-free', label: t('landing.tiers.tierFree'), price: 0, minutes: t('landing.tiers.fiveMinTrial') },
    { id: 'studio-basic', label: t('landing.tiers.tierBasic'), price: 19, minutes: t('landing.tiers.minPerMo', { count: 30 }) },
    { id: 'studio-pro', label: t('landing.tiers.tierPro'), price: 49, minutes: t('landing.tiers.minPerMo', { count: 120 }) },
  ]

  const { data: credits } = useQuery({ queryKey: ['credits'], queryFn: fetchCredits, staleTime: 30_000 })
  const { data: profile } = useQuery({ queryKey: ['profile'], queryFn: fetchProfile, staleTime: Infinity })

  const [displayName, setDisplayName] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [loading, setLoading] = useState<string | null>(null)
  const [tab, setTab] = useState<'extension' | 'studio' | 'bundle'>('studio')

  // Seed display name from profile
  if (profile?.displayName && !displayName && !saving) {
    setDisplayName(profile.displayName)
  }

  const totalMinutes = credits
    ? credits.studioMinutesTotal + credits.topupMinutesTotal
    : 0
  const usedMinutes = credits
    ? credits.studioMinutesUsed + credits.topupMinutesUsed + credits.studioMinutesReserved
    : 0
  const usedPct = totalMinutes > 0 ? Math.min(100, (usedMinutes / totalMinutes) * 100) : 0

  const handleSave = async () => {
    setSaving(true)
    try {
      await updateProfile({ displayName })
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } finally { setSaving(false) }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await deleteAccount()
      await signOut()
      nav('/')
    } finally { setDeleting(false) }
  }

  const handleSubscribe = async (tierId: string) => {
    setLoading(tierId)
    try {
      const { url } = await createCheckout(tierId, `${window.location.origin}/usage?success=1`, `${window.location.origin}/usage`)
      window.location.href = url
    } catch { setLoading(null) }
  }

  const handleTopup = async (minutes: number) => {
    setLoading(`topup-${minutes}`)
    try {
      const { url } = await createTopupCheckout(minutes, `${window.location.origin}/usage?success=1`, `${window.location.origin}/usage`)
      window.location.href = url
    } catch { setLoading(null) }
  }

  const tiers = tab === 'extension' ? extensionTiers : studioTiers

  return (
    <div style={{ maxWidth: 540, margin: '0 auto' }}>
      {/* ── Current plan ──────────────────────────────────────────────── */}
      <div className="section-head"><span className="section-title">{t('usage.currentPlan')}</span></div>
      <div className="info-card" style={{ marginBottom: 22, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: 'var(--silk)', opacity: .45 }} />
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 3, color: 'var(--text-app)' }}>{t('usage.freePlan')}</div>
          <div style={{ fontSize: 12, color: 'var(--sub)' }}>{t('usage.freeDesc')}</div>
        </div>
        <div style={{ marginTop: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--sub)', marginBottom: 6 }}>
            <span>{usedMinutes.toFixed(1)} {t('usage.minUsed')}</span>
            <span>{totalMinutes} {t('usage.minTotal')}</span>
          </div>
          <div style={{ height: 5, background: 'rgba(255,255,255,.05)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${usedPct}%`, background: 'var(--silk-r)', borderRadius: 3, position: 'relative' }}>
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg,transparent,rgba(255,255,255,.3),transparent)', animation: 'shb 2s infinite' }} />
            </div>
          </div>
        </div>
      </div>

      {/* ── Upgrade options ────────────────────────────────────────────── */}
      <div className="section-head"><span className="section-title">{t('usage.upgradeOptions')}</span></div>

      {/* Extension / Studio / Bundle tabs */}
      <div style={{ display: 'inline-flex', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border-app)', marginBottom: 16 }}>
        {(['extension', 'studio', 'bundle'] as const).map(t2 => (
          <button
            key={t2}
            onClick={() => setTab(t2)}
            style={{
              padding: '8px 18px', fontSize: 12, fontWeight: 700,
              border: 'none',
              background: tab === t2 ? 'linear-gradient(135deg, #FF006E, #FFBE0B)' : 'transparent',
              color: tab === t2 ? '#fff' : 'var(--sub)',
              cursor: 'pointer', fontFamily: 'inherit', transition: 'all .18s',
            }}
          >
            {t2 === 'extension' ? <>{t('pricing.tab.extension', 'Extension')} <span className="coming-soon-badge" style={{ marginLeft: 4, fontSize: 8, padding: '2px 5px' }}>{t('comingSoon')}</span></> : t2 === 'studio' ? t('pricing.tab.studio', 'Studio Dub') : t('pricing.tab.bundle', 'Bundle')}
          </button>
        ))}
      </div>

      {/* Tier rows or bundle rows */}
      {tab !== 'bundle' ? (
        <div className="info-card" style={{ marginBottom: 22, padding: 0, overflow: 'hidden', ...(tab === 'extension' ? { opacity: 0.55, pointerEvents: 'none' as const } : {}) }}>
          {tiers.map((tier, i) => {
            const isLoading = loading === tier.id
            const isExt = tab === 'extension'
            return (
              <div key={tier.id} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '13px 16px',
                borderTop: i > 0 ? '1px solid var(--border-app)' : 'none',
              }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-app)', minWidth: 42 }}>{tier.label}</span>
                <span style={{ fontSize: 12, color: 'var(--sub)', flex: 1 }}>{tier.minutes}</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-app)' }}>
                  {tier.price === 0 ? t('landing.tiers.free') : t('landing.tiers.bundlePrice', { price: tier.price })}
                </span>
                {tier.price > 0 && (
                  <button
                    className="btn-dub"
                    style={{ padding: '5px 12px', fontSize: 11 }}
                    disabled={isLoading || isExt}
                    onClick={() => void handleSubscribe(tier.id)}
                  >
                    {isExt ? t('comingSoon') : isLoading ? '...' : t('usage.choose', 'Choose')}
                  </button>
                )}
              </div>
            )
          })}
        </div>
      ) : (
        <div className="info-card" style={{ marginBottom: 22, padding: 0, overflow: 'hidden', opacity: 0.55, pointerEvents: 'none' }}>
          {BUNDLES.map((b, i) => {
            return (
              <div key={b.id} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '13px 16px',
                borderTop: i > 0 ? '1px solid var(--border-app)' : 'none',
              }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-app)', flex: 1 }}>
                  {t('pricing.bundle.labelShort', { ext: t('landing.tiers.tier' + b.ext), studio: t('landing.tiers.tier' + b.studio) })}
                </span>
                <span style={{
                  fontSize: 11, fontWeight: 700,
                  background: 'linear-gradient(135deg,#FF006E,#FFBE0B)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                }}>
                  {t('pricing.bundle.save', { amount: b.savings })}
                </span>
                <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-app)' }}>
                  {t('landing.tiers.bundlePrice', { price: b.price })}
                </span>
                <button
                  className="btn-dub"
                  style={{ padding: '5px 12px', fontSize: 11 }}
                  disabled
                >
                  {t('comingSoon')}
                </button>
              </div>
            )
          })}
        </div>
      )}

      {/* ── Top-up packs (studio minutes) ──────────────────────────────── */}
      <div className="section-head"><span className="section-title">{t('usage.topupTitle')}</span></div>
      <div className="info-card" style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 12, color: 'var(--sub)', marginBottom: 13 }}>{t('usage.topupDesc')}</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {TOPUPS.map(({ minutes, price }) => (
            <button
              key={minutes}
              className="btn-new-dub"
              disabled={loading === `topup-${minutes}`}
              onClick={() => void handleTopup(minutes)}
            >
              {loading === `topup-${minutes}` ? '...' : t('pricing.topups.buyLabel', { minutes, price })}
            </button>
          ))}
        </div>
      </div>

      {/* ── Account settings ───────────────────────────────────────────── */}
      <div className="section-head"><span className="section-title">{t('settings.profileSection')}</span></div>
      <div className="info-card" style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <div className="field-label" style={{ marginBottom: 6 }}>{t('settings.displayName')}</div>
          <input
            type="text"
            className="input"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            style={{ maxWidth: 320, fontSize: 13, padding: '8px 12px', borderRadius: 'var(--rsm)', border: '1px solid var(--border-app)', background: 'rgba(255,255,255,.04)', color: 'var(--text-app)' }}
          />
        </div>
        <button className="btn-dub" style={{ fontSize: 12, padding: '8px 18px' }} onClick={() => void handleSave()} disabled={saving}>
          {saving ? t('settings.saving') : saved ? t('settings.saved') : t('settings.saveBtn')}
        </button>
      </div>

      {/* ── Danger zone ────────────────────────────────────────────────── */}
      <div className="section-head"><span className="section-title">{t('settings.dangerZone')}</span></div>
      <div className="info-card" style={{ borderColor: 'rgba(239,68,68,.2)' }}>
        <div style={{ fontSize: 12, color: 'var(--sub)', marginBottom: 12 }}>{t('settings.deleteAccountDesc')}</div>
        {!confirmDelete ? (
          <button
            className="btn-dub"
            style={{ background: 'transparent', border: '1px solid var(--color-error, #ef4444)', color: 'var(--color-error, #ef4444)', fontSize: 12, padding: '8px 18px' }}
            onClick={() => setConfirmDelete(true)}
          >
            {t('settings.deleteAccountBtn')}
          </button>
        ) : (
          <button
            className="btn-dub"
            style={{ background: '#ef4444', fontSize: 12, padding: '8px 18px' }}
            onClick={() => void handleDelete()}
            disabled={deleting}
          >
            {deleting ? t('settings.deleting') : t('settings.deleteConfirmBtn')}
          </button>
        )}
      </div>
    </div>
  )
}
